import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar'
import { Card, CardContent } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { toast } from 'sonner'
import { Upload, Camera, Search, AlertTriangle, UserCheck } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function CreateProfile() {
  const navigate = useNavigate()
  const { token } = useAuth()
  const { isDarkMode } = useTheme()
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    reddit: '',
    youtube: '',
    tiktok: '',
    website: '',
  })
  const [profileImage, setProfileImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  
  // Unclaimed profile search state
  const [unclaimedSearch, setUnclaimedSearch] = useState('')
  const [unclaimedResults, setUnclaimedResults] = useState([])
  const [isSearchingUnclaimed, setIsSearchingUnclaimed] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  
  // Check for existing claim status on mount
  useEffect(() => {
    checkExistingClaim()
  }, [])
  
  const checkExistingClaim = async () => {
    try {
      const response = await axios.get(`${API}/profiles/claim/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.data.has_claim) {
        // User has an active claim, redirect to verification page
        toast.info('You have an active profile claim. Redirecting to verification...')
        navigate('/claim-verification')
      }
    } catch (err) {
      // No claim found, continue with normal creation
    }
  }
  
  const searchUnclaimedProfiles = async () => {
    if (!unclaimedSearch.trim()) return
    
    setIsSearchingUnclaimed(true)
    try {
      const response = await axios.get(`${API}/profiles/unclaimed/search?q=${encodeURIComponent(unclaimedSearch)}`)
      setUnclaimedResults(response.data.profiles || [])
    } catch (err) {
      toast.error('Failed to search unclaimed profiles')
    } finally {
      setIsSearchingUnclaimed(false)
    }
  }
  
  const handleClaimProfile = async (username) => {
    setIsClaiming(true)
    try {
      await axios.post(
        `${API}/profiles/claim`,
        { username },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success('Profile claim initiated! Complete verification to finish.')
      navigate('/claim-verification')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim profile')
    } finally {
      setIsClaiming(false)
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image too large (max 5MB)')
        return
      }
      
      setProfileImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Create profile
      await axios.post(
        `${API}/profiles`,
        {
          name: formData.name,
          username: formData.username,
          bio: formData.bio || null,
          social_links: {
            facebook: formData.facebook || null,
            instagram: formData.instagram || null,
            twitter: formData.twitter || null,
            linkedin: formData.linkedin || null,
            reddit: formData.reddit || null,
            youtube: formData.youtube || null,
            tiktok: formData.tiktok || null,
            website: formData.website || null,
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      // Upload image if selected
      if (profileImage) {
        const formDataImage = new FormData()
        formDataImage.append('file', profileImage)
        
        await axios.post(`${API}/profiles/upload-image`, formDataImage, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      }

      toast.success('Profile created successfully!')
      navigate('/home')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create profile')
    } finally {
      setIsLoading(false)
    }
  }

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className={`min-h-screen py-12 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      <div className="max-w-2xl mx-auto">
        <div className="absolute top-4 right-4">
          <DarkModeToggle />
        </div>
        <div className={`rounded-3xl shadow-2xl p-8 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 backdrop-blur-sm border-purple-100'} border`}>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Create Your Profile
          </h1>
          <p className={`mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{fontFamily: 'Inter, sans-serif'}}>
            Set up your Public Respect profile to get started
          </p>

          {/* Unclaimed Profile Search Section */}
          <Card className={`mb-8 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border-2`}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <h3 className={`font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  Has someone already created a profile for you?
                </h3>
              </div>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                Search for unclaimed profiles by name. If someone scouted you, you can claim your profile instead of creating a new one.
              </p>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="text"
                    placeholder="Search by name..."
                    value={unclaimedSearch}
                    onChange={(e) => setUnclaimedSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchUnclaimedProfiles()}
                    className={`pl-10 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    data-testid="unclaimed-search-input"
                  />
                </div>
                <Button 
                  type="button"
                  onClick={searchUnclaimedProfiles}
                  disabled={isSearchingUnclaimed || !unclaimedSearch.trim()}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  data-testid="unclaimed-search-button"
                >
                  {isSearchingUnclaimed ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {/* Unclaimed Search Results */}
              {unclaimedResults.length > 0 && (
                <div className="space-y-2">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Found {unclaimedResults.length} unclaimed profile(s):
                  </p>
                  {unclaimedResults.map((profile) => (
                    <div 
                      key={profile.username}
                      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <Avatar className="w-12 h-12 flex-shrink-0 border-2 border-yellow-400">
                          {profile.profile_image ? (
                            <AvatarImage src={profile.profile_image} alt={profile.name} />
                          ) : (
                            <AvatarFallback className={`${isDarkMode ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-700'} font-bold`}>
                              {profile.name?.charAt(0) || '?'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {profile.name}
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            @{profile.username}
                          </p>
                        </div>
                        <Badge className="bg-yellow-500 text-white text-xs flex-shrink-0">
                          Unclaimed
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        onClick={() => handleClaimProfile(profile.username)}
                        disabled={isClaiming}
                        className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`claim-profile-${profile.username}`}
                      >
                        {isClaiming ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        ) : (
                          <UserCheck className="w-4 h-4 mr-2" />
                        )}
                        Claim This Profile
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {unclaimedSearch && unclaimedResults.length === 0 && !isSearchingUnclaimed && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No unclaimed profiles found. You can create a new profile below.
                </p>
              )}
            </CardContent>
          </Card>

          <form data-testid="create-profile-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Upload */}
            <div className="flex flex-col items-center mb-6">
              <Label className={`mb-3 ${isDarkMode ? 'text-gray-200' : ''}`}>Profile Image</Label>
              <div className="relative">
                <Avatar className={`w-32 h-32 border-4 ${isDarkMode ? 'border-purple-400' : 'border-purple-200'}`}>
                  {imagePreview ? (
                    <AvatarImage src={imagePreview} alt="Profile" />
                  ) : (
                    <AvatarFallback className="text-3xl font-bold bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                      {formData.name ? getInitials(formData.name) : <Camera className="w-12 h-12" />}
                    </AvatarFallback>
                  )}
                </Avatar>
                <label htmlFor="profile-image" className="absolute bottom-0 right-0 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-full cursor-pointer shadow-lg">
                  <Upload className="w-5 h-5" />
                  <input
                    id="profile-image"
                    data-testid="profile-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max size: 5MB</p>
            </div>

            <div>
              <Label htmlFor="name" className={isDarkMode ? 'text-gray-200' : ''}>Name *</Label>
              <Input
                data-testid="profile-name-input"
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
              />
            </div>

            <div>
              <Label htmlFor="username" className={isDarkMode ? 'text-gray-200' : ''}>Username *</Label>
              <Input
                data-testid="profile-username-input"
                id="username"
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                placeholder="Choose a unique username"
              />
            </div>

            <div>
              <Label htmlFor="bio" className={isDarkMode ? 'text-gray-200' : ''}>Bio</Label>
              <Textarea
                data-testid="profile-bio-input"
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                rows={3}
                placeholder="Tell us about yourself"
              />
            </div>

            <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-700' : ''}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>Social Links</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="facebook" className={isDarkMode ? 'text-gray-200' : ''}>Facebook</Label>
                  <Input
                    data-testid="profile-facebook-input"
                    id="facebook"
                    type="url"
                    value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://facebook.com/yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram" className={isDarkMode ? 'text-gray-200' : ''}>Instagram</Label>
                  <Input
                    data-testid="profile-instagram-input"
                    id="instagram"
                    type="url"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="twitter" className={isDarkMode ? 'text-gray-200' : ''}>X (Twitter)</Label>
                  <Input
                    data-testid="profile-twitter-input"
                    id="twitter"
                    type="url"
                    value={formData.twitter}
                    onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://x.com/yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedin" className={isDarkMode ? 'text-gray-200' : ''}>LinkedIn</Label>
                  <Input
                    data-testid="profile-linkedin-input"
                    id="linkedin"
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="reddit" className={isDarkMode ? 'text-gray-200' : ''}>Reddit</Label>
                  <Input
                    data-testid="profile-reddit-input"
                    id="reddit"
                    type="url"
                    value={formData.reddit}
                    onChange={(e) => setFormData({ ...formData, reddit: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://reddit.com/u/yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="youtube" className={isDarkMode ? 'text-gray-200' : ''}>YouTube</Label>
                  <Input
                    data-testid="profile-youtube-input"
                    id="youtube"
                    type="url"
                    value={formData.youtube}
                    onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://youtube.com/@yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="tiktok" className={isDarkMode ? 'text-gray-200' : ''}>TikTok</Label>
                  <Input
                    data-testid="profile-tiktok-input"
                    id="tiktok"
                    type="url"
                    value={formData.tiktok}
                    onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://tiktok.com/@yourprofile"
                  />
                </div>
                <div>
                  <Label htmlFor="website" className={isDarkMode ? 'text-gray-200' : ''}>Website</Label>
                  <Input
                    data-testid="profile-website-input"
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>

            <Button
              data-testid="submit-profile-button"
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl"
            >
              {isLoading ? 'Creating...' : 'Create Profile'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}